import { LitElement, html, css } from 'lit'
import {customElement, property, query, state} from 'lit/decorators.js'
import { classMap } from 'lit/directives/class-map.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/dropdown/dropdown.js';
import '@shoelace-style/shoelace/dist/components/menu/menu.js';
import '@shoelace-style/shoelace/dist/components/menu-item/menu-item.js';
import '@shoelace-style/shoelace/dist/components/divider/divider.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';

/**
 * @example :
 * [
 *    { id: 'HOME', label: 'Home', href: '#', active: true, type: 'link' },
 *    { label: 'About', href: '#', type: 'link' },
 *    { 
 *      label: 'Services', 
 *      type: 'dropdown',
 *      items: [
 *        { label: 'Web Design', href: '#web-design' },
 *        { label: 'Development', href: '#development' },
 *        { label: 'Consulting', href: '#consulting', divider: true },
 *        { label: 'Support', href: '#support' }
 *      ]
 *    },
 *    { label: 'Contact', href: '#', type: 'link' }
 *  ];
 */

interface MenuItem {
  id?: string;
  label: string;
  href?: string;
  active?: boolean;
  type?: 'link' | 'dropdown';
  terminate?: boolean;
  items?: Array<{
    id?: string;
    label: string;
    href: string;
    divider?: boolean;
  }>;
}

@customElement('vxc-navbar')
export class TNavbar extends LitElement {

  static styles = css`
    :host {
      display: block;
      font-family: var(--sl-font-sans);
      --vx-menu-background-color: rgba(18, 18, 18, 0.9);
    }

    .navbar {
      background-color: var(--vx-menu-background-color);
      border-bottom: 1px solid var(--sl-color-neutral-200);
      padding: var(--sl-spacing-2x-small) var(--sl-spacing-medium);
      position: relative;
      box-shadow: var(--sl-shadow-small);
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
    }

    .navbar-container {
      display: flex;
      align-items: center;
      justify-content: space-between;
      max-width: 90%;
      margin: 0 auto;
      width: 100%;
    }

    .navbar-brand {
      display: flex;
      align-items: center;
      text-decoration: none;
      color: white;
      font-size: var(--sl-font-size-large);
      font-weight: var(--sl-font-weight-semibold);
      transition: color var(--sl-transition-fast) ease;
    }

    .navbar-brand:hover {
      color: var(--sl-color-primary-600);
    }

    .logo {
      height: 30px;
      width: auto;
      margin-right: var(--sl-spacing-small);
    }

    .navbar-nav {
      display: flex;
      list-style: none;
      margin: 0;
      padding: 0;
      align-items: center;
      gap: var(--sl-spacing-small);
    }

    .nav-item {
      position: relative;
    }

    .nav-link {
      text-decoration: none;
      color: white;
      padding: var(--sl-spacing-small) var(--sl-spacing-medium);
      border-radius: var(--sl-border-radius-medium);
      transition: all var(--sl-transition-fast) ease;
      display: block;
      font-weight: var(--sl-font-weight-medium);
    }

    .nav-link:hover {
      color: var(--sl-color-primary-600);
      background-color: var(--sl-color-primary-50);
    }

    .nav-link.active {
      color: var(--sl-color-primary-600);
      background-color: var(--sl-color-primary-100);
      font-weight: var(--sl-font-weight-semibold);
      -webkit-border-radius: 5px;
      border-radius: 5px;
    }

    .dropdown-trigger {
      display: flex;
      align-items: center;
      gap: var(--sl-spacing-2x-small);
      background: none;
      border: none;
      color: var(--sl-color-neutral-600);
      padding: var(--sl-spacing-small) var(--sl-spacing-medium);
      border-radius: var(--sl-border-radius-medium);
      transition: all var(--sl-transition-fast) ease;
      cursor: pointer;
      font-weight: var(--sl-font-weight-medium);
      font-size: var(--sl-font-size-medium);
      font-family: inherit;
    }

    .dropdown-trigger:hover {
      color: var(--sl-color-primary-600);
      background-color: var(--sl-color-primary-50);
    }

    .navbar-toggler {
      display: none;
    }

    .navbar-collapse {
      display: flex;
      align-items: center;
    }

    /* Mobile styles */
    @media (max-width: 768px) {
      .navbar-toggler {
        display: block;
      }

      .navbar-collapse {
        position: absolute;
        top: 100%;
        left: 0;
        right: 0;
        background-color: var(--sl-color-neutral-0);
        border-top: 1px solid var(--sl-color-neutral-200);
        box-shadow: var(--sl-shadow-large);
        max-height: 0;
        overflow: hidden;
        transition: max-height var(--sl-transition-medium) ease;
        z-index: 1000;
      }

      .navbar-collapse.show {
        max-height: 400px;
      }

      .navbar-nav {
        flex-direction: column;
        padding: var(--sl-spacing-medium) 0;
        width: 100%;
        gap: 0;
      }

      .nav-item {
        width: 100%;
      }

      .nav-link,
      .dropdown-trigger {
        padding: var(--sl-spacing-medium) var(--sl-spacing-large);
        border-bottom: 1px solid var(--sl-color-neutral-100);
        border-radius: 0;
        justify-content: space-between;
        width: 100%;
        text-align: left;
      }

      .nav-item:last-child .nav-link,
      .nav-item:last-child .dropdown-trigger {
        border-bottom: none;
      }

      /* Mobile dropdown styles */
      .mobile-dropdown {
        display: block;
        background-color: var(--sl-color-neutral-50);
        border-top: 1px solid var(--sl-color-neutral-200);
      }

      .mobile-dropdown sl-menu-item {
        padding-left: var(--sl-spacing-2x-large);
      }

      .desktop-dropdown {
        display: none;
      }
    }

    @media (min-width: 769px) {
      .mobile-dropdown {
        display: none;
      }

      .desktop-dropdown {
        display: block;
      }
    }

    /* Custom dropdown menu styling */
    sl-dropdown::part(panel) {
      border: 1px solid var(--sl-color-neutral-200);
      box-shadow: var(--sl-shadow-large);
    }

    sl-menu-item::part(base) {
      transition: background-color var(--sl-transition-fast) ease;
    }

    sl-menu-item:hover::part(base) {
      background-color: var(--sl-color-primary-50);
      color: var(--sl-color-primary-600);
    }
  `;

    @property({ type: String, attribute: 'logo-src'         }) logoSrc = ''
    @property({ type: String, attribute: 'logo-alt'         }) logoAlt = 'Logo'
    @property({ type: String, attribute: 'brand'            }) title   = 'Brand'
    @property({ type: Array,  attribute: 'menu-items'       }) menuItems: Array<MenuItem> = []
    @property({ type: String, attribute: 'data-app-var-name'}) varName = 'voxy-ai-selected-section'
    @state() private isMenuOpen = false

    private toggleMenu() {
        this.isMenuOpen = !this.isMenuOpen
    }

    private activate(menuId: string): void {
        console.log('activate :', menuId)
        if(menuId) {  
            this.menuItems.forEach( item => item.active = false )
            let menuItem: MenuItem|undefined = this.menuItems.find( item => item.id === menuId)
            if(menuItem) {   
                menuItem.active = true
                this.menuItems = [...this.menuItems]
                if(menuItem.terminate)      localStorage.removeItem(this.varName)
                else                        localStorage.setItem(this.varName, menuId)
                const detail = {
                    id    : menuItem.id,
                    label : menuItem.label,
                    href  : menuItem.href
                }        
                this.dispatchEvent(new CustomEvent('menu-item-click', { detail, bubbles: true, composed: true }))
            }
        }
    }

    private handleMenuItemClick(event: Event, item: MenuItem) {
        console.log('handleClick', event.target)
        event.preventDefault()
        const target = event.target as HTMLAnchorElement
        
        // activate Item
        this.activate(target.id)

        // Close mobile menu when item is clicked
        this.isMenuOpen = false;        
    }

    private handleDropdownItemClick(event: Event) {
        event.preventDefault()
        const menuItem = event.target as any;
        const href = menuItem.value;
        const label = menuItem.textContent?.trim();
        const detail = {
            id    : menuItem.id,
            label : menuItem.textContent?.trim(),
            href  : menuItem.value
        }
        
        // Close mobile menu
        this.isMenuOpen = false;
        
        // Dispatch custom event
        this.dispatchEvent(new CustomEvent('menu-item-click', { detail, bubbles: true, composed: true }));
    }

    private renderDesktopDropdown(item: MenuItem) {
        return html`
        <sl-dropdown id="${item.id}" class="desktop-dropdown">
            <button slot="trigger" class="dropdown-trigger">
            ${item.label}
            <sl-icon name="chevron-down" style="font-size: 0.8em;"></sl-icon>
            </button>
            <sl-menu @sl-select="${this.handleDropdownItemClick}">
            ${item.items?.map(subItem => html`
                ${subItem.divider ? html`<sl-divider></sl-divider>` : ''}
                <sl-menu-item id="${subItem.id}" value="${subItem.href}">${subItem.label}</sl-menu-item>
            `)}
            </sl-menu>
        </sl-dropdown>
        `;
    }

    private renderMobileDropdown(item: MenuItem) {
        return html`
        <div class="mobile-dropdown">
            <button id="${item.id}" class="dropdown-trigger" @click="${() => this.toggleMobileDropdown(item)}">
            ${item.label}
            <sl-icon name="chevron-down" style="font-size: 0.8em;"></sl-icon>
            </button>
            <sl-menu class="mobile-dropdown-menu" @sl-select="${this.handleDropdownItemClick}">
            ${item.items?.map(subItem => html`
                ${subItem.divider ? html`<sl-divider></sl-divider>` : ''}
                <sl-menu-item id="${subItem.id}" value="${subItem.href}">${subItem.label}</sl-menu-item>
            `)}
            </sl-menu>
        </div>
        `;
    }

    private toggleMobileDropdown(item: MenuItem) {
        // Simple toggle for mobile dropdown - you might want to enhance this
        const event = new CustomEvent('mobile-dropdown-toggle', {
            detail: { item },
            bubbles: true,
            composed: true
        });
        this.dispatchEvent(event);
    }

    updated(changedProperties: Map<string | number | symbol, unknown>): void {
        console.log('Component has been rendered for the first time.')
        super.updated(changedProperties)
        if(changedProperties.has('varName')) {
            const active = localStorage.getItem(this.varName)
            console.log('found active menu :', active)
            if(active)  this.activate(active)
        }
    }

    render() {
        const collapseClasses = {
            'navbar-collapse': true,
            'show': this.isMenuOpen
        }

        console.log('menu items :', this.menuItems)

        return html`
        <nav class="navbar">
            <div class="navbar-container">
            <a href="#" class="navbar-brand" @click="${(e: Event) => this.handleMenuItemClick(e, { label: this.title, href: '#' })}">
                ${this.logoSrc ? html`<img src="${this.logoSrc}" alt="${this.logoAlt}" class="logo">` : ''}
                ${this.title}
            </a>

            <sl-button 
                class="navbar-toggler" 
                variant="text" 
                size="medium"
                @click="${this.toggleMenu}"
            >
                <sl-icon name="${this.isMenuOpen ? 'x' : 'list'}" library="voxy-icons" style="font-size: 1.2em;"></sl-icon>
            </sl-button>

            <div class="${classMap(collapseClasses)}">
                <ul class="navbar-nav">
                ${this.menuItems.map(item => {
                    if (item.type === 'dropdown') {
                    return html`
                        <li class="nav-item">
                        ${this.renderDesktopDropdown(item)}
                        ${this.renderMobileDropdown(item)}
                        </li>
                    `;
                    } else {
                    return html`
                        <li class="nav-item">
                        <a 
                            id="${item.id}"
                            href="${item.href || '#'}" 
                            class="nav-link ${item.active ? 'active' : ''}"
                            @click="${(e: Event) => this.handleMenuItemClick(e, item)}"
                        >
                            ${item.label}
                        </a>
                        </li>
                    `;
                    }
                })}
                </ul>
            </div>
            </div>
        </nav>
        `;
    }
}

declare global {
  interface HTMLElementTagNameMap {
    'vxc-navbar': TNavbar;
  }
}